import React, { useState } from "react";
import { format } from "date-fns";
import { fr as frLocale, enGB as enLocale } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Editorial dark/gold date picker built on top of shadcn Calendar + Popover.
 * - Returns ISO yyyy-mm-dd through `onChange(value)` for backend compatibility.
 * - Stays on-brand (transparent input, gold focus, serif label inside popover).
 */
const DatePickerInput = ({ value, onChange, testId, placeholder, minDate }) => {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);

  const dateLocale = lang === "fr" ? frLocale : enLocale;
  const selected = value ? new Date(value) : undefined;

  const display = selected
    ? format(selected, "PPP", { locale: dateLocale })
    : placeholder ||
      (lang === "fr" ? "Sélectionner une date" : "Select a date");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="kw-input flex items-center justify-between text-left w-full focus:outline-none"
        >
          <span className={value ? "text-[#F2EBE5]" : "text-[#F2EBE5]/35"}>
            {display}
          </span>
          <CalendarIcon
            className="w-4 h-4 text-[#C5A059] flex-shrink-0"
            strokeWidth={1.5}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="bg-[#0a0a0a] border border-[#C5A059]/30 text-[#F2EBE5] p-0"
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <Calendar
          mode="single"
          locale={dateLocale}
          selected={selected}
          onSelect={(d) => {
            if (d) {
              onChange(format(d, "yyyy-MM-dd"));
              setOpen(false);
            } else {
              onChange("");
            }
          }}
          disabled={minDate ? { before: minDate } : undefined}
          initialFocus
          className="bg-[#0a0a0a]"
          classNames={{
            day_selected:
              "bg-[#C5A059] text-[#050505] hover:bg-[#C5A059] hover:text-[#050505] focus:bg-[#C5A059]",
            day_today: "border border-[#C5A059] text-[#C5A059]",
            head_cell:
              "text-[#A19E9C] uppercase tracking-[0.15em] text-[10px] font-normal w-9",
            day: "h-9 w-9 p-0 font-light text-[#F2EBE5] hover:bg-[#C5A059]/15 hover:text-[#C5A059] rounded-none",
            caption_label:
              "font-serif-display text-lg text-[#F2EBE5] capitalize",
            nav_button:
              "h-7 w-7 bg-transparent text-[#C5A059] hover:bg-[#C5A059]/10",
            cell: "p-0",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePickerInput;
